import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { ClientCombobox } from "@/components/shared/ClientCombobox";
import { EditPaymentModal, EditPaymentFormData } from "@/components/payments/EditPaymentModal";
import { clientService } from "@/services/client";
import {
  paymentService,
  Payment,
  EPaymentMethod,
  EPaymentStatus,
  PaymentFilters,
} from "@/services/payment";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ChevronLeft, ChevronRight, Eye, Pencil } from "lucide-react";
import { EownerType } from "@/services/subscription";
import { useUrlFilters, useUrlPage } from "@/hooks/use-url-filters";

const PAGE_SIZE = 10;

const paymentMethodLabels: Record<EPaymentMethod, string> = {
  [EPaymentMethod.TRANSFER]: "Transferencia",
  [EPaymentMethod.CASH]: "Efectivo",
  [EPaymentMethod.NEQUI]: "Nequi",
  [EPaymentMethod.DAVIPLATA]: "Daviplata",
  [EPaymentMethod.BANCOLOMBIA]: "Bancolombia",
    [EPaymentMethod.PAYPAL]: "PayPal",
    [EPaymentMethod.BINANCE]: "Binance",
    [EPaymentMethod.YAPE]: "Yape",
};

const paymentStatusLabels: Record<EPaymentStatus, string> = {
  [EPaymentStatus.COMPLETED]: "Completado",
  [EPaymentStatus.PENDING]: "Pendiente",
  [EPaymentStatus.CANCELLED]: "Cancelado",
  [EPaymentStatus.REFUNDED]: "Reembolsado",
};

const paymentStatusStyles: Record<EPaymentStatus, string> = {
  [EPaymentStatus.COMPLETED]: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  [EPaymentStatus.PENDING]: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  [EPaymentStatus.CANCELLED]: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  [EPaymentStatus.REFUNDED]: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
};

const DEFAULT_FILTERS = {
  clientId: "",
  method: "ALL",
  status: "ALL",
};

function generatePaginationNumbers(
  currentPage: number,
  totalPages: number
): (number | string)[] {
  const delta = 1;
  const range: (number | string)[] = [];

  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= currentPage - delta && i <= currentPage + delta)
    ) {
      range.push(i);
    } else if (range[range.length - 1] !== "...") {
      range.push("...");
    }
  }

  return range;
}

export default function Payments() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [urlFilters, setUrlFilters] = useUrlFilters(DEFAULT_FILTERS);
  const { clientId, method: methodFilter, status: statusFilter } = urlFilters;
  const [currentPage, setCurrentPage] = useUrlPage();
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Hidratar el cliente seleccionado a partir del clientId en la URL
  const { data: selectedClientResponse } = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => clientService.getOne(clientId),
    enabled: !!clientId,
  });
  const selectedClient = clientId ? selectedClientResponse?.data ?? null : null;

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: EditPaymentFormData }) =>
      paymentService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      setIsEditModalOpen(false);
      setEditingPayment(null);
      toast.success("Pago actualizado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const filters: PaymentFilters = {
    page: currentPage,
    limit: PAGE_SIZE,
    clientId: clientId || undefined,
    paymentMethod:
      methodFilter !== "ALL" ? (methodFilter as EPaymentMethod) : undefined,
    status:
      statusFilter !== "ALL" ? (statusFilter as EPaymentStatus) : undefined,
    ownerType: EownerType.CLIENT
  };

  const {
    data: paymentsResponse,
    isLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: [
      "payments",
      currentPage,
      clientId,
      methodFilter,
      statusFilter,
      filters.ownerType
    ],
    queryFn: () => paymentService.getAll(filters),
  });

  const payments = paymentsResponse?.data || [];
  const pagination = paymentsResponse?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const totalItems = pagination?.totalItems || 0;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <DashboardLayout title="Pagos" subtitle="Gestiona todos los pagos">
      <div className="space-y-6 animate-fade-in">
        {/* Filters Bar */}
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1 max-w-xs">
            <label className="text-sm font-medium text-muted-foreground mb-1 block">
              Cliente
            </label>
            <ClientCombobox
              value={selectedClient}
              onChange={(client) => setUrlFilters({ clientId: client?.id ?? "" })}
            />
          </div>

          <div className="w-full sm:w-[180px]">
            <label className="text-sm font-medium text-muted-foreground mb-1 block">
              Metodo de pago
            </label>
            <Select value={methodFilter} onValueChange={(value) => setUrlFilters({ method: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Todos los metodos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos los metodos</SelectItem>
                {Object.values(EPaymentMethod).map((method) => (
                  <SelectItem key={method} value={method}>
                    {paymentMethodLabels[method]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full sm:w-[180px]">
            <label className="text-sm font-medium text-muted-foreground mb-1 block">
              Estado
            </label>
            <Select value={statusFilter} onValueChange={(value) => setUrlFilters({ status: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Todos los estados" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos los estados</SelectItem>
                {Object.values(EPaymentStatus).map((status) => (
                  <SelectItem key={status} value={status}>
                    {paymentStatusLabels[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>
              Error al cargar los pagos. Por favor, intenta de nuevo.
            </AlertDescription>
          </Alert>
        )}

        {/* Payments Table */}
        <div className="card-elevated overflow-hidden relative">
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}

          {isLoading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground mt-2">Cargando pagos...</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-semibold">Cliente</TableHead>
                  <TableHead className="font-semibold text-right">
                    Monto
                  </TableHead>
                  <TableHead className="font-semibold">
                    Fecha de pago
                  </TableHead>
                  <TableHead className="font-semibold">Metodo</TableHead>
                  <TableHead className="font-semibold">Estado</TableHead>
                  <TableHead className="font-semibold">Notas</TableHead>
                  <TableHead className="font-semibold text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow
                    key={payment.id}
                    className="transition-colors hover:bg-muted/30"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-medium text-xs">
                            {payment.client?.name
                              ?.split(" ")
                              .map((n) => n[0])
                              .join("") || "?"}
                          </span>
                        </div>
                        <span className="font-medium text-foreground">
                          {payment.client?.name || "—"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      ${Number(payment.amount).toFixed(0)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(payment.paymentDate)}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        {paymentMethodLabels[payment.paymentMethod]}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${paymentStatusStyles[payment.status]}`}
                      >
                        {paymentStatusLabels[payment.status]}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">
                      {payment.notes || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {payment.subscriptionId && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              navigate(`/subscriptions/${payment.subscriptionId}`)
                            }
                          >
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            setEditingPayment(payment);
                            setIsEditModalOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {!isLoading && payments.length === 0 && (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">
                No se encontraron pagos
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Intenta ajustar los filtros
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Mostrando {payments.length} de {totalItems} pagos
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage(Math.max(1, currentPage - 1))
                }
                disabled={currentPage === 1 || isFetching}
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </Button>
              <div className="flex items-center gap-1">
                {generatePaginationNumbers(currentPage, totalPages).map(
                  (page, idx) =>
                    page === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-2 text-muted-foreground"
                      >
                        ...
                      </span>
                    ) : (
                      <Button
                        key={page}
                        variant={
                          currentPage === page ? "default" : "outline"
                        }
                        size="sm"
                        className="w-9"
                        onClick={() => setCurrentPage(page as number)}
                        disabled={isFetching}
                      >
                        {page}
                      </Button>
                    )
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages || isFetching}
              >
                Siguiente
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <EditPaymentModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        payment={editingPayment}
        onSave={(data) => {
          if (editingPayment) {
            updateMutation.mutate({ id: editingPayment.id, data });
          }
        }}
        isPending={updateMutation.isPending}
      />
    </DashboardLayout>
  );
}
